/** Types of the subject taxonomy and of the `taxonomy` edge function's lookups (one row type each, as the SQL function returns it). */

import type { SectionKey } from "./section_key.ts";

/** How deep a node sits in the subject skeleton. */
export type TaxonomyLevel = "domain" | "field";

/**
 * Path-style URN identifying a taxonomy node: urn:rc:taxonomy:<domain>[:<field>[:<topic>]]. A plain
 * string: the skeleton lives in the `taxonomy_nodes` table, so whether one is a known node is
 * answered at runtime.
 */
export type TaxonomyUrn = string;

/** The shape of a taxonomy URN, checked at the edge; whether the skeleton has it is checked against the table. */
export const TAXONOMY_URN_PATTERN = /^urn:rc:taxonomy:[a-z][a-z-]*(:[a-z][a-z-]*)*$/;

/** A node of the subject skeleton: a domain, or a field within a domain. */
export interface TaxonomyNode {
  urn: TaxonomyUrn;
  parentUrn: TaxonomyUrn | null;
  level: TaxonomyLevel;
  /** English name, rendered as is while the platform is English-only. */
  enName: string;
}

/** The subject skeleton as `GET /expert-insights/taxonomy-nodes` returns it. */
export interface TaxonomyNodesResponse {
  nodes: TaxonomyNode[];
}

/** A journal with the tier someone decided and NLM's MEDLINE answer; both null when unknown. */
export interface TaxonomyJournal {
  nlm_id: string | null;
  title: string;
  tier: string | null;
  medline_indexed: boolean | null;
}

/** A funding party and the type of the party that controls it, if any. */
export interface TaxonomyParty {
  name: string;
  type: string;
  controller_type: string | null;
}

/** A string known to name no party. */
export interface TaxonomyNonParty {
  name: string;
}

/** A section title mapped to a section key, or flagged as noise. */
export interface TaxonomySectionTitle {
  title: string;
  section_key: string | null;
  noise: boolean;
}

/** The vocabularies whose missing entries are stored as gaps. */
export const TAXONOMY_GAP_KINDS = [
  "journal",
  "funding_agency",
  "section_title",
  "mesh_tree_prefix",
] as const;
export type TaxonomyGapKind = (typeof TAXONOMY_GAP_KINDS)[number];

/** What a gap's judge needs beyond its kind and value: a journal's title, a MeSH descriptor's name and tree numbers. */
export interface TaxonomyGapContext {
  label: string | null;
  tree_numbers: string[];
}

/** A vocabulary entry one document lacked, as the extraction reports it. */
export interface TaxonomyGapReport {
  kind: TaxonomyGapKind;
  value: string;
  context: TaxonomyGapContext | null;
}

/** An open gap picked for judging. */
export interface OpenTaxonomyGap {
  id: string;
  kind: TaxonomyGapKind;
  value: string;
  context: TaxonomyGapContext | null;
}

/** What judging an open gap came to, as the database records it. */
export type TaxonomyGapJudgementOutcome = "known" | "decided" | "escalated";

/** The tiers a journal can be decided into. */
export const TAXONOMY_JOURNAL_TIERS = ["top_tier", "reputable", "predatory"] as const;
export type TaxonomyJournalTier = (typeof TAXONOMY_JOURNAL_TIERS)[number];

/** What a funding party can be: ROR's organisation types, plus an individual. */
export const TAXONOMY_PARTY_TYPES = [
  "company",
  "government",
  "nonprofit",
  "education",
  "healthcare",
  "facility",
  "archive",
  "other",
  "individual",
] as const;
export type TaxonomyPartyType = (typeof TAXONOMY_PARTY_TYPES)[number];

/** Where a gap stands: judged by Tangrowth, or by a person, until its entry is filled. */
export const TAXONOMY_GAP_STATUSES = ["open", "escalated", "decided", "filled"] as const;
export type TaxonomyGapStatus = (typeof TAXONOMY_GAP_STATUSES)[number];

/** A journal to insert: the tier decided for it, and NLM's answer on MEDLINE indexing. */
export interface TaxonomyJournalEntry {
  entry: "journal";
  tier: TaxonomyJournalTier | null;
  medline_indexed: boolean | null;
}

/** A funding party to insert, with the name of the known company that controls it, if any. */
export interface TaxonomyPartyEntry {
  entry: "party";
  type: TaxonomyPartyType;
  controlled_by: string | null;
}

/** A string that names no party. */
export interface TaxonomyNonPartyEntry {
  entry: "non_party";
}

/** A section title mapped to a section key, or flagged as noise: exactly one of the two. */
export interface TaxonomySectionTitleEntry {
  entry: "section_title";
  section_key: SectionKey | null;
  noise: boolean;
}

/** A MeSH tree prefix to insert for each skeleton node named, mapped to it or ignored by it. */
export interface TaxonomyMeshPrefixEntry {
  entry: "mesh_prefix";
  prefix: string;
  urns: TaxonomyUrn[];
  ignored: boolean;
}

/** The vocabulary entry a decided gap inserts, as the judge returns it and a person enters it. */
export type TaxonomyGapEntry =
  | TaxonomyJournalEntry
  | TaxonomyPartyEntry
  | TaxonomyNonPartyEntry
  | TaxonomySectionTitleEntry
  | TaxonomyMeshPrefixEntry;

/** The entry types each gap kind can be decided with. */
export const TAXONOMY_GAP_ENTRY_TYPES: Record<TaxonomyGapKind, TaxonomyGapEntry["entry"][]> = {
  journal: ["journal"],
  funding_agency: ["party", "non_party"],
  section_title: ["section_title"],
  mesh_tree_prefix: ["mesh_prefix"],
};

/** The shape of a MeSH tree prefix: a letter and two digits, then dot-separated groups of three. */
export const MESH_TREE_PREFIX_PATTERN = /^[A-Z]\d{2}(\.\d{3})*$/;

/** The vocabulary tables the corpus sync moves, in the order they must be written: a node before
 *  the prefixes on it, a journal and a party before nothing that needs them. */
export const TAXONOMY_SYNC_TABLES = [
  "nodes",
  "mesh_prefixes",
  "journals",
  "parties",
  "non_parties",
  "section_titles",
] as const;
export type TaxonomySyncTable = (typeof TAXONOMY_SYNC_TABLES)[number];

/** One entry of a vocabulary table as the sync carries it: copied as it came and checked by the
 *  database's own constraints when written. */
export type TaxonomySyncEntry = Record<string, unknown>;

/** One page of a vocabulary table, with how many entries the table holds. */
export interface TaxonomySyncPage {
  items: TaxonomySyncEntry[];
  total: number;
}

/** What the triage did over a period, for the Slack digest. */
export interface TaxonomyGapDigest {
  filled: number;
  newly_escalated: number;
  escalated_total: number;
  open_total: number;
  /** Up to five of the newly escalated gaps, newest first. */
  escalated_sample: { kind: TaxonomyGapKind; value: string; reason: string }[];
}

/** A document waiting to be relabelled, with the kinds of the filled gaps it waits on. */
export interface TaxonomyGapDocumentToRelabel {
  source_id: string;
  url: string;
  kinds: TaxonomyGapKind[];
}

/** A node of the subject skeleton with the MeSH prefixes it maps to and ignores. */
export interface TaxonomySkeletonNode {
  urn: string;
  parent_urn: string | null;
  level: string;
  en_name: string;
  mesh_prefixes: string[];
  ignored_mesh_prefixes: string[];
}

/** The `taxonomy` request parameter, service version: names the taxonomy edge function Tangrowth reads
 *  the vocabulary from and the token to call it with. Mirrors Tangrowth's ServiceTaxonomy. */
export interface ServiceTaxonomyParameter {
  kind: "service";
  /** The taxonomy function's URL; its routes (journals, parties, ...) are below it. */
  base_url: string;
  /** Bearer token for the function. Never log it or put it in an error message. */
  token: string;
}
